/* eslint-disable @typescript-eslint/unbound-method */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import {
  call, put, take, takeLatest,
} from 'redux-saga/effects';
import { actions } from 'Slice/ServiceLinesSlice';

import axios from 'Utils/Axios';
import { API, SLALERTS, SLCOMPANIES } from 'Utils/Constants';
import { getCurrentTime, valueToObject } from 'Utils/UtilityFunctions';
import {
  ServiceLine, ServiceLineDetailsResponse, ServiceLineWidgetResponse, SLEditCompanyRequest, SLAddCompanyResponse, SLAddResponse,
  SLFollowedResponse, SLNewsRequest, SLRemoveResponse,
  SLRegionsResponse,
} from 'Types/ServiceLinesTypes';
import { PayloadAction } from '@reduxjs/toolkit';
import { actions as helperActions } from 'Slice/HelperSlice';
import { GetCompaniesRequest } from '../Types/RequestBodies';
import { CompaniesDataResponse } from '../Types/CompaniesTypes';
import { SRIFiltersRequest } from '../Types/SRITypes';
import { actions as flurryActions } from 'Slice/HelperSlice';
import { GlobalResponse } from '../Types/ResponseBodies';

export function* getServiceLines() {
  const startTime = getCurrentTime();
  try {
    const apiResponse: SLFollowedResponse = yield call(
      axios.post,
      API.GET_SERVICELINE,
      {},
    );
    if (apiResponse?.status === true) {
      const { response } = apiResponse;
      yield put(actions.setServiceLines(response));
      yield put(helperActions.callFlurryEvent(
        valueToObject(
          'serviceline_page_load',
          startTime,
          {
            number_of_servicelines: response.followedServiceLines.length
              + response.recommendedServiceLines.length || 0,
          },
        ),
      ));
    }
  } catch (err) {
    console.log(err);
  }
}

export function* removeServiceLines(dispatched: PayloadAction<ServiceLine[]>) {
  const startTime = getCurrentTime();
  const data = dispatched.payload.map((v) => +v.id);
  const serviceLineName = dispatched.payload.map((v) => v.name);
  try {
    const apiResponse: SLRemoveResponse = yield call(
      axios.post,
      API.REMOVE_SERVICELINE,
      { topicId: data.join(', ') },
    );
    if (apiResponse?.status === true) {
      yield put(helperActions.setNotification({
        message: apiResponse?.message,
        proTip: false,
        rightPlacement: false,
        groupId: 0,
        classnameFlag: true,
      }));
      yield put(actions.removedtServiceLines(dispatched.payload));
      yield put(helperActions.callFlurryEvent(
        valueToObject(
          'servicelines_removed',
          startTime,
          {
            industry_name: serviceLineName.join(','),
            ...dispatched.payload.length > 1 && {
              serviceline_id: data.join(','),
            },
            ...dispatched.payload.length === 1 && {
              serviceline_id: data.join(','),
            },
          },
        ),
      ));
    } else {
      yield put(helperActions.setNotification({
        message: apiResponse?.message,
        proTip: false,
        rightPlacement: true,
        groupId: 0,
        classnameFlag: false,
      }));
    }
  } catch (err) {
    console.log(err);
  }
}

export function* addingServiceLine(dispatched: PayloadAction<ServiceLine>) {
  const startTime = getCurrentTime();
  try {
    const apiResponse: SLAddResponse = yield call(
      axios.post,
      API.ADD_SERVICELINE,
      { topicId: dispatched.payload.id },
    );
    if (apiResponse?.status === true) {
      yield put(helperActions.setNotification({
        message: apiResponse?.message,
        proTip: false,
        rightPlacement: false,
        groupId: 0,
        classnameFlag: true,
      }));
      if (dispatched.payload.type === 'detailPage') {
        yield put(actions.addedServiceLineDetail(dispatched.payload));
      } else {
        yield put(actions.addedServiceLine(dispatched.payload));
      }
      yield put(helperActions.callFlurryEvent(
        valueToObject(
          'servicelines_added',
          startTime,
          {
            serviceline_id: dispatched.payload.id,
            serviceline_name: dispatched.payload.name,
          },
        ),
      ));
    }
    if (apiResponse?.status === false) {
      yield put(helperActions.setNotification({
        message: apiResponse?.message,
        proTip: false,
        rightPlacement: true,
        groupId: 0,
        classnameFlag: false,
      }));
      yield put(actions.addedServiceLineError(dispatched.payload));
    }
  } catch (err) {
    console.log(err);
    // Reset followLoading state on error
    yield put(actions.addedServiceLineError(dispatched.payload));
  }
}

export function* getServiceLineWidgets(dispatched: PayloadAction<SLNewsRequest>) {
  const startTime = getCurrentTime();
  try {
    const response: ServiceLineWidgetResponse = yield call(
      axios.post,
      API.GET_SERVICELINEWIDGETS,
      dispatched?.payload,
    );

    if (response?.status === true) {
      yield put(actions.setServiceLineWidgets({ ...response?.response, kpmgUser: dispatched?.payload.kpmgMode }));
      yield put(helperActions.callFlurryEvent(
        valueToObject(
          'serviceline_news',
          startTime,
          {
            serviceline_id: dispatched.payload.id,
            serviceline_name: response?.response?.name || '',
          },
        ),
      ));
    }
  } catch (err) {
    console.log(err);
  }
}

export function* getServiceLineDetails(dispatched: PayloadAction<SLNewsRequest>) {
  const startTime = getCurrentTime();
  const { payload } = dispatched;
  const AppliedFilters = payload?.filters?.filter(
    (item) => item.isSelected,
  ).map((item) => ({
    id: item.filterId,
    type: item.filterTypeName,
  }));
  const AppliedCustomFilters = payload?.customFilters?.map((item) => {
    if (item.filterTypeName === SLALERTS) {
      const selectedSubs = (item.subFilters || [])
        .filter((sub) => sub.isSelected)
        .map((sub) => sub.id);

      if (selectedSubs.length) {
        return {
          id: item.filterId,
          type: item.filterTypeName,
          subFilters: selectedSubs.join(","),
        };
      }
    } else {
      if (item.filterTypeName === SLCOMPANIES && item.isSelected === true) {
        return {
          id: item.filterId,
          type: item.filterTypeName,
        }
      }
    }
  }).filter(Boolean);
  const obj = {
    id: payload.id,
    page: payload.page,
    ...payload.kpmgMode && { kpmgMode: payload.kpmgMode },
    ...payload.widget_name && { widget_name: payload.widget_name },
    ...payload.filters && {
      filters: AppliedFilters,
    },
    ...payload.customFilters && {
      customFilters: AppliedCustomFilters,
    },
    ...payload.dateRangeFilter && { dateRangeFilter: payload.dateRangeFilter },
    ...payload.regions && { regions: payload.regions }
  };
  try {
    const response: ServiceLineDetailsResponse = yield call(
      axios.post,
      API.GET_SERVICELINEDETAILS,
      obj,
    );

    if (response?.status === true) {
      if (payload?.widget_name) {
        yield put(actions.setWidgetNewsDetails({ ...response?.response, kpmgUser: obj.kpmgMode }));
        if (response?.response?.articles?.length < 1) {
          yield put(actions.setWidgetArticlesEnded(payload?.widget_name));
        }
      } else {
        yield put(actions.setNewsDetails(response?.response));
        if (response?.response?.articles?.length < 1) {
          yield put(actions.setNewsArticlesEnded());
        }
      }

      yield put(helperActions.callFlurryEvent(
        valueToObject(
          'serviceline_news_page_load',
          startTime,
          {
            serviceline_id: payload?.id,
            page: payload?.page,
            ...payload?.widget_name && { widget_name: payload?.widget_name },
            serviceline_name: response?.response?.name || '',
            ...AppliedFilters?.length
            && {
              number_of_filters_applied: AppliedFilters.length,
              filter_applied: AppliedFilters,
            },
          },
        ),
      ));
    }
  } catch (err) {
    console.log(err);
  }
}

export function* getSLCompanies(dispatched: PayloadAction<GetCompaniesRequest>) {
  const startTime = getCurrentTime();
  const payload = dispatched?.payload;
  const data: any = {
    offset: payload.offset,
    ...payload.screenName && { screenName: payload.screenName },
    ...payload.serviceLineId && { serviceLineId: payload.serviceLineId },
  };
  try {
    const response: CompaniesDataResponse = yield call(
      axios.post,
      API.GET_LAUNCHPAD_COMPANIES,
      data,
    );
    if (response?.status === true) {
      yield put(actions.setSLCompanies(response));
    }
  } catch (err) {
    console.log(err);
  }
}

export function* editCompanyInServiceLine(dispatched: PayloadAction<SLEditCompanyRequest>) {
  const startTime = getCurrentTime();
  const payload = dispatched?.payload;
  try {
    const response: SLAddCompanyResponse = yield call(
      axios.post,
      API.EDIT_COMPANY_IN_SERVICELINE,
      payload
    )
    if (response?.status === true) {
      yield put(actions.setAddCompanyInServiceLine());
      yield put(flurryActions.setNotification({
        message: "Selected companies have been saved.",
        proTip: false,
        rightPlacement: false,
        groupId: 0,
        classnameFlag: true,
      }))
    }
  } catch (err) {
    console.log(err);
  }
}

export function* refreshAfterAddCompany(dispatched: PayloadAction<{ serviceLineId: number, widgetName: string, dateRangeFilter: SRIFiltersRequest, regions: string }>) {
  const { serviceLineId, widgetName, dateRangeFilter, regions } = dispatched.payload;

  yield put(actions.getServiceLineWidgets({ id: serviceLineId, kpmgMode: true }));
  yield take(actions.setServiceLineWidgets.type);
  yield put(actions.setFilters(dateRangeFilter));
  yield put(actions.getServiceLineDetails({
    id: serviceLineId,
    widget_name: widgetName,
    page: 1,
    dateRangeFilter,
    kpmgMode: true,
    regions: regions,

  }));
}

export function* getRegions(dispatched: PayloadAction<{ serviceLineId: number }>) {
  const payload = dispatched?.payload;
  try {
    const response: SLRegionsResponse = yield call(
      axios.post,
      API.SERVICELINES_REGIONS,
      payload
    )
    if (response?.status === true) {
      yield put(actions.setRegions(response?.response))
    }
  } catch (err) {
    console.log(err);
  }
}

export function* servicelineSaga() {
  yield takeLatest(actions.getServiceLines.type, getServiceLines);
  yield takeLatest(actions.removeServiceLines.type, removeServiceLines);
  yield takeLatest(actions.addingServiceLine.type, addingServiceLine);
  yield takeLatest(actions.getServiceLineWidgets.type, getServiceLineWidgets);
  yield takeLatest(actions.getServiceLineDetails.type, getServiceLineDetails);
  yield takeLatest(actions.getSLCompanies.type, getSLCompanies);
  yield takeLatest(actions.editCompanyInServiceLine.type, editCompanyInServiceLine);
  yield takeLatest(actions.refreshAfterAddCompany.type, refreshAfterAddCompany);
  yield takeLatest(actions.getRegions.type, getRegions);
}